import {
  Stack,
  Text,
  Menu,
  IconButton,
  MenuList,
  MenuItem,
  MenuButton,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
  Heading,
  VStack,
  HStack,
} from '@chakra-ui/react';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { $, $$, useModule, StarberryComponentContext } from '@starberry/components';

import { UserObjectContext } from 'core/services/store/userobject/store';
import * as UserObjectActions from 'core/services/store/userobject/actions';
import UserObjectServices from 'core/services/store/userobject/services';
import { createSearchUrl } from 'core/helpers/search';

import FormDialog from 'core/components/FormDialog';

const SearchListControls = ({
  id,
  viewResultsText,
  editSearchText,
  deleteSearchText,
  stopAlertsText,
  resumeAlertsText,
  unsubscribeAlertText,
  params,
  website,
}) => {
  const global = useContext(StarberryComponentContext);
  const [state, dispatch] = useContext(UserObjectContext);
  const [editing, setEditing] = useState(false);
  const userServices = UserObjectServices(dispatch, UserObjectActions);
  const {
    isOpen: isConfirmationOpen,
    onOpen: onOpenConfirmation,
    onClose: onCloseConfirmation,
  } = useDisclosure();

  const handleUpdate = useCallback(
    (values) => {
      userServices.updateUserobject('savedSearches', {
        params: {
          ...values,
        },
        id,
      });
      setEditing(false);
    },
    [id, userServices]
  );

  const data = useMemo(() => {
    return state?.savedSearches?.find(({ id: itemID }) => itemID === id);
  }, [state, id]);

  const handleToggleAlerts = useCallback(() => {
    userServices.updateUserobject('savedSearches', {
      state: !data.state,
      id,
    });
    onCloseConfirmation();
  }, [data, userServices, id, onCloseConfirmation]);

  return (
    <Menu>
      {editing && (
        <FormDialog
          title="Editing Search"
          isOpen={true}
          fields={global?.constants?.forms?.createSavedSearch}
          data={data.params}
          onClose={() => setEditing(false)}
          onSubmit={handleUpdate}
        />
      )}

      {isConfirmationOpen && (
        <ConfirmUnsubscribeAlerts
          unsubscribeAlertText={unsubscribeAlertText}
          onClose={onCloseConfirmation}
          onConfirm={handleToggleAlerts}
        />
      )}
      <MenuButton
        as={IconButton}
        aria-label="Options"
        icon={<Icon as={global?.constants?.icons?.moreActions} boxSize={6} />}
        isRound
        variant="ghost"
      />
      <MenuList>
        <MenuItem
          onClick={() => {
            let url = `${website}${createSearchUrl(params)}`;
            window.location = url;
          }}
          icon={<Icon as={global?.constants?.icons?.saveSearchViewResults} boxSize={6} />}
        >
          {viewResultsText}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEditing((isEditing) => !isEditing);
          }}
          icon={<Icon as={global?.constants?.icons?.saveSearchEdit} boxSize={6} />}
        >
          {editSearchText}
        </MenuItem>
        <MenuItem
          onClick={() => {
            UserObjectServices(dispatch, UserObjectActions).deleteUserObject('savedSearches', {
              id: id,
            });
          }}
          icon={<Icon as={global?.constants?.icons?.saveSearchDelete} boxSize={6} />}
        >
          {deleteSearchText}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (data.state) {
              onOpenConfirmation();
            } else {
              handleToggleAlerts();
            }
          }}
          icon={<Icon as={global?.constants?.icons?.saveSearchStopAlerts} boxSize={6} />}
        >
          {data?.state ? stopAlertsText : resumeAlertsText}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

const SearchContent = ({ content }) => {
  return content?.map(({ id, title, param }) => {
    return (
      <Stack key={`${id}-${title}-${param}`} spacing={0}>
        <Text fontWeight="bold">{title}</Text>
        <Text fontSize="0.875rem">{param}</Text>
      </Stack>
    );
  });
};

const ConfirmUnsubscribeAlerts = ({ onClose, onConfirm, unsubscribeAlertText }) => {
  const PrimaryButton = useModule('PrimaryButton');
  if (!PrimaryButton) {
    return null;
  }
  return (
    <Modal blockScrollOnMount={false} isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody>
          <VStack spacing={5} py={4}>
            <Heading size="md">{unsubscribeAlertText}</Heading>
            <HStack>
              <PrimaryButton width="100px" onClick={() => onConfirm}>
                YES
              </PrimaryButton>
              <PrimaryButton width="100px" onClick={() => onClose}>
                NO
              </PrimaryButton>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const SearchContentIcon = () => {
  const { constants } = useContext(StarberryComponentContext);

  return <Icon as={constants.icons.saveSearchAction} boxSize={6} />;
};

const SearchListItem = {
  $: 'Grid',
  props: {
    templateColumns: 'auto 1fr 50px',
    gap: 6,
    autoFlow: '',
    overflow: 'visible',
    textAlign: 'left',
    justifyContent: 'center',
    mb: 5,
  },
  titleProps: {},
  children: (props, context) => {
    return [
      {
        $: 'Flex',
        props: {
          m: '0',
          p: '0',
          alignItems: 'center',
          justifyContent: 'center',
          onClick: props.onClick,
          opacity: !context.data.state ? 0.5 : 1,
        },
        children: [
          {
            $: 'Center',
            props: {
              backgroundColor: $('constants.colors.grey4'),
              w: [12, null, null, 20],
              h: [12, null, null, 20],
              borderRadius: '100%',
            },
            children: [
              {
                component: props.icon || SearchContentIcon,
              },
            ],
          },
        ],
      },
      {
        $: 'Flex',
        props: {
          flexDirection: 'column',
          justifyContent: 'center',
          opacity: !context.data.state ? 0.5 : 1,
        },
        children: [
          {
            $: 'H3',
            props: {
              as: 'h3',
              fontSize: $$('constants.body.bodySize.small', 'constants.body.bodySize.large'),
              fontWeight: 'normal',
              mb: [0, null, null, 2],
              ...props.titleProps,
            },
            children: props?.data?.title,
          },
          {
            $: 'Stack',
            props: {
              spacing: 6,
              direction: 'row',
              d: ['none', null, null, 'flex'],
            },
            children: {
              component: SearchContent,
              props: {
                content: [...props?.data?.content],
              },
            },
          },
        ],
      },
      {
        component: SearchListControls,
        props: {
          id: props?.data.id,
          params: props?.data.params,
          website: context?.constants?.['website-url'],
          viewResultsText: $(
            'constants.translations.savedSearch.VIEW_RESULTS_TEXT',
            'View Results'
          ),
          editSearchText: $('constants.translations.savedSearch.EDIT_RESULTS_TEXT', 'Edit Search'),
          deleteSearchText: $(
            'constants.translations.savedSearch.DELETE_SEARCH_TEXT',
            'Delete Search'
          ),
          stopAlertsText: $('constants.translations.savedSearch.STOP_ALERTS_TEXT', 'Stop Alerts'),
          resumeAlertsText: $(
            'constants.translations.savedSearch.RESUME_ALERTS_TEXT',
            'Resume Alerts'
          ),
          unsubscribeAlertText: $(
            'constants.translations.savedSearch.UNSUBSCRIBE_ALERT_TEXT',
            'Unsubscribe from property alerts'
          ),
        },
      },
    ];
  },
};

export default SearchListItem;
